import { useCallback, useEffect, useState } from "react";
import type { AnzeigePhase } from "@offsite/shared";
import { api } from "@/api/client";
import { useStore, useWorkshop } from "@/state/store";
import { useOffsiteConfig } from "@/engine/app/offsite-context";

/**
 * Zeitleiste im Kopf des Pults (Briefing Prompt-Teil 4.2): alle Phasen als
 * Segmente in Soll-Breite, laufende Phase hervorgehoben, daneben Gruppe-Rest,
 * Phase-Rest und die Abweichung zum Soll (`+1:10` = hinter Plan). Buttons:
 * „Gruppe starten", „+2 Min" (nur Arbeitsphase), „Nächste Phase".
 */

const REIHENFOLGE: AnzeigePhase[] = [
  "einfuehrung",
  "thema",
  "prompten",
  "arbeitsphase",
  "generierung",
  "vortrag",
  "ergebnisse",
  "abschluss",
];

const LABEL: Record<AnzeigePhase, string> = {
  einfuehrung: "Einführung",
  thema: "Thema",
  prompten: "Prompten",
  arbeitsphase: "Arbeitsphase",
  generierung: "Generierung",
  vortrag: "Vortrag",
  ergebnisse: "Ergebnisse",
  abschluss: "Abschluss",
};

function fmt(restSek: number): string {
  const ueberzug = restSek < 0;
  const s = Math.abs(Math.trunc(restSek));
  return `${ueberzug ? "+" : ""}${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function AdminZeitleiste() {
  const config = useOffsiteConfig();
  const ablauf = config.stationsAblauf;
  const { state, dispatch } = useStore();
  const token = state.auth.token;
  const phaseConfig = useWorkshop().phase;
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const dauern = ablauf?.phaseDauernSec;

  const anzeige: AnzeigePhase | null = (() => {
    if (!phaseConfig || phaseConfig.phase === "waiting") return null;
    if (phaseConfig.anzeigePhase) return phaseConfig.anzeigePhase;
    switch (phaseConfig.phase) {
      case "einfuehrung":
        return "einfuehrung";
      case "working":
        return "arbeitsphase";
      case "overnight":
        return "generierung";
      case "reveal":
        return "ergebnisse";
      case "abschluss":
        return "abschluss";
      default:
        return null;
    }
  })();

  const gruppeStarten = useCallback(async () => {
    if (!dauern) return;
    setBusy(true);
    try {
      const p = await api.phase.set(token, "einfuehrung", undefined, {
        anzeigePhase: "einfuehrung",
        phaseDauerSec: dauern.einfuehrung,
        gruppeStart: true,
      });
      dispatch({ type: "phase/set", phase: p });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  }, [token, dispatch, dauern]);

  const naechstePhase = useCallback(async () => {
    if (!dauern || !anzeige) return;
    const idx = REIHENFOLGE.indexOf(anzeige);
    const next = REIHENFOLGE[idx + 1];
    if (!next) return;
    setBusy(true);
    try {
      if (next === "generierung") {
        try {
          await api.overnight.trigger(token);
        } catch {
          /* 409 = läuft schon */
        }
      }
      const kern =
        next === "einfuehrung" || next === "thema"
          ? ("einfuehrung" as const)
          : next === "prompten" || next === "arbeitsphase"
            ? ("working" as const)
            : next === "generierung" || next === "vortrag"
              ? ("overnight" as const)
              : next === "ergebnisse"
                ? ("reveal" as const)
                : ("abschluss" as const);
      const p = await api.phase.set(
        token,
        kern,
        next === "arbeitsphase" ? dauern.arbeitsphase : undefined,
        { anzeigePhase: next, phaseDauerSec: dauern[next] },
      );
      dispatch({ type: "phase/set", phase: p });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  }, [token, dispatch, dauern, anzeige]);

  const plusZwei = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.admin.setTimer(token, 120, "extend");
      dispatch({ type: "phase/set", phase: r.phase });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  }, [token, dispatch]);

  if (!ablauf || !dauern) return null;

  const now = Date.now();
  const gesamtSoll = REIHENFOLGE.reduce((a, p) => a + dauern[p], 0);
  const gruppeRest =
    phaseConfig.gruppeStartedAt !== undefined
      ? (phaseConfig.gruppeDauerSec ?? 1500) -
        (now - phaseConfig.gruppeStartedAt) / 1000
      : null;
  const phaseRest =
    phaseConfig.phaseStartedAt !== undefined &&
    phaseConfig.phaseDauerSec !== undefined
      ? phaseConfig.phaseDauerSec - (now - phaseConfig.phaseStartedAt) / 1000
      : null;
  /**
   * Abweichung zum Soll: tatsächlicher Beginn der laufenden Phase gegen ihren
   * geplanten Beginn (Summe der Soll-Dauern davor). Positiv = hinter Plan.
   */
  const abweichung = (() => {
    if (
      !anzeige ||
      phaseConfig.gruppeStartedAt === undefined ||
      phaseConfig.phaseStartedAt === undefined
    )
      return null;
    const sollOffset = REIHENFOLGE.slice(0, REIHENFOLGE.indexOf(anzeige)).reduce(
      (a, p) => a + dauern[p],
      0,
    );
    return (
      (phaseConfig.phaseStartedAt - phaseConfig.gruppeStartedAt) / 1000 -
      sollOffset
    );
  })();

  return (
    <div className="admin-zeitleiste" data-testid="admin-zeitleiste">
      <div className="admin-zeitleiste-segmente" aria-hidden="true">
        {REIHENFOLGE.map((p) => {
          const aktiv = p === anzeige;
          const anteil =
            aktiv && phaseRest !== null && dauern[p] > 0
              ? Math.min(1, Math.max(0, 1 - phaseRest / dauern[p]))
              : 0;
          return (
            <span
              key={p}
              className={`admin-zeitleiste-segment${aktiv ? " is-aktiv" : ""}${
                anzeige && REIHENFOLGE.indexOf(p) < REIHENFOLGE.indexOf(anzeige)
                  ? " is-vorbei"
                  : ""
              }`}
              style={{ flexGrow: dauern[p] / gesamtSoll }}
              title={`${LABEL[p]} · Soll ${fmt(dauern[p])}`}
            >
              {aktiv && (
                <span
                  className="admin-zeitleiste-marker"
                  style={{ left: `${Math.round(anteil * 100)}%` }}
                />
              )}
              <span className="admin-zeitleiste-seglabel">{LABEL[p]}</span>
            </span>
          );
        })}
      </div>

      <div className="admin-zeitleiste-zahlen">
        {gruppeRest !== null && (
          <span data-testid="zeitleiste-gruppe">Gruppe {fmt(gruppeRest)}</span>
        )}
        {phaseRest !== null && anzeige && (
          <span data-testid="zeitleiste-phase">
            {LABEL[anzeige]} {fmt(phaseRest)}
          </span>
        )}
        {abweichung !== null && Math.abs(abweichung) >= 1 && (
          <span
            data-testid="zeitleiste-abweichung"
            title="Abweichung zum Soll — plus heißt hinter Plan"
          >
            Soll {abweichung > 0 ? "+" : "−"}
            {fmt(Math.abs(abweichung)).replace("+", "")}
          </span>
        )}
      </div>

      <div className="admin-zeitleiste-aktionen">
        {/* Die beiden Reset-Handgriffe des Tages, direkt im Pult-Kopf:
            Gruppenwechsel nach jeder Runde, Tages-Reset morgens/abends. */}
        <button
          type="button"
          className="btn"
          data-testid="zeitleiste-naechste-gruppe"
          disabled={busy}
          onClick={() => {
            if (!window.confirm("Nächste Gruppe? Prompts und Ergebnisse dieser Runde werden archiviert.")) return;
            setBusy(true);
            void (async () => {
              try {
                await api.admin.resetGroup(token, phaseConfig.gruppeLabel);
                const p = await api.phase.set(token, "waiting");
                dispatch({ type: "phase/set", phase: p });
              } catch (e) {
                console.error(e);
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          Nächste Gruppe
        </button>
        <button
          type="button"
          className="btn"
          data-testid="zeitleiste-tagesreset"
          disabled={busy}
          onClick={() => {
            if (!window.confirm("Tages-Reset? ALLES wird archiviert und die Station beginnt von vorn. Ergebnis-Links und Codes bleiben gültig.")) return;
            setBusy(true);
            void (async () => {
              try {
                await api.admin.reset(token, "all", `Tagesabschluss ${new Date().toLocaleDateString("de-DE")}`);
                const p = await api.phase.set(token, "waiting");
                dispatch({ type: "phase/set", phase: p });
              } catch (e) {
                console.error(e);
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          Tages-Reset
        </button>
        {phaseConfig.gruppeStartedAt === undefined ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void gruppeStarten()}
            disabled={busy}
            data-testid="zeitleiste-gruppe-starten"
          >
            Gruppe starten
          </button>
        ) : (
          <>
            {anzeige === "arbeitsphase" && (
              <button
                type="button"
                className="btn"
                onClick={() => void plusZwei()}
                disabled={busy}
                data-testid="zeitleiste-plus"
                title="Verlängert Phase und Gruppe um zwei Minuten"
              >
                +2 Min
              </button>
            )}
            {anzeige && anzeige !== "abschluss" && (
              <button
                type="button"
                className="btn"
                onClick={() => void naechstePhase()}
                disabled={busy}
                data-testid="zeitleiste-naechste"
              >
                Nächste Phase
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
